import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import amplitude from 'amplitude-js';
import { changeFormFieldValue } from 'apps/auth/actions/formAction';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import { CALCULATOR_SCREEN } from 'apps/auth/components/send/SendConstants';
import {
    PAYEE_CLOSE_MATCH_SHOWN,
    PAYEE_CLOSE_MATCH_WARNING_SHOWN,
    PAYEE_CONTINUE_ANYWAY_CLICKED,
    PAYEE_GO_BACK_EDIT_CLICKED,
    PAYEE_MISMATCH_SHOWN,
    PAYEE_MISMATCH_WARNING_SHOWN,
    PAYEE_PROCEED_DETAILS_CLICKED,
    PAYEE_USE_BANK_DETAILS_CLICKED,
} from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { TO_NAME, TO_NAME_F, TO_RECIPIENT_OTHER, TO_RECIPIENT_BIZ, TO_RECIPIENT_SELF } from 'apps/auth/constants/fieldsName';
import { resolveArguments } from 'apps/auth/helpers/utils';
import { useFields } from 'apps/auth/hooks/selectors';

const FIRST_POPUP = 'first';
const SECOND_POPUP = 'second';

const PayeePopup = (props) => {
    const {
        extra: { typeOfTransfer = '', textFirstPopup = '', textSecondPopup = '', isBlocked = false, useThisName = '', useThisNameF = '', code = '' } = {},
    } = props;
    const [screen, setScreen] = useState(FIRST_POPUP);
    const fields = useFields(props.formName);

    const calcFirstLink = useThisName ? resolveArguments(props.useThisNameButton, useThisName) : props.bankAccountEdit;
    const calcFirstIcon = useThisName ? 'retry' : 'arrow-back-circular';

    const isCloseMatchCase = (code = '') => {
        return code === 'CLOSE_MATCH' || code === 'BUSINESS_ACCOUNT_CLOSE_MATCH' || code === 'PERSONAL_ACCOUNT_CLOSE_MATCH';
    };

    const isNotMatchCase = (code = '') => {
        return code === 'NOT_MATCHED';
    };

    useEffect(() => {
        if (isCloseMatchCase(code) && screen === FIRST_POPUP) {
            amplitude.getInstance().logEvent(PAYEE_CLOSE_MATCH_SHOWN);
        }
        if (isCloseMatchCase(code) && screen === SECOND_POPUP) {
            amplitude.getInstance().logEvent(PAYEE_CLOSE_MATCH_WARNING_SHOWN);
        }
        if (isNotMatchCase(code) && screen === FIRST_POPUP) {
            amplitude.getInstance().logEvent(PAYEE_MISMATCH_SHOWN);
        }
        if (isNotMatchCase(code) && screen === SECOND_POPUP) {
            amplitude.getInstance().logEvent(PAYEE_MISMATCH_WARNING_SHOWN);
        }
    }, [screen]);

    const setRecipientNameNameF = (recipientName, recipientNameF, fields, typeOfTransfer) => {
        if (fields[TO_NAME] && fields[TO_NAME_F]) {
            props.changeFormFieldValue(props.formName, recipientName, TO_NAME);
            props.changeFormFieldValue(props.formName, recipientNameF, TO_NAME_F);
        }

        if (typeOfTransfer === 'business') {
            props.changeFormFieldValue(props.formName, recipientName, TO_RECIPIENT_OTHER);
            props.changeFormFieldValue(props.formName, recipientName, TO_RECIPIENT_BIZ);
        }
        if (typeOfTransfer === 'other') {
            props.changeFormFieldValue(props.formName, recipientName, TO_RECIPIENT_OTHER);
        }

        if (typeOfTransfer === 'self') {
            props.changeFormFieldValue(props.formName, recipientName, TO_RECIPIENT_SELF);
        }
    };

    const handleClickFirstScreenFirstButton = (e) => {
        e.preventDefault();
        props.popupRef?.current?.close();
        if (useThisName && isCloseMatchCase(code)) {
            setRecipientNameNameF(useThisName, useThisNameF, fields, typeOfTransfer);
            props.sendParamToStateMachine ? props.sendParamToStateMachine({ type: 'NEXT' }) : props.setNextScreen(CALCULATOR_SCREEN);
            setScreen(FIRST_POPUP);
            amplitude.getInstance().logEvent(PAYEE_USE_BANK_DETAILS_CLICKED);
        } else {
            amplitude.getInstance().logEvent(PAYEE_GO_BACK_EDIT_CLICKED);
        }
    };

    const handleClickFirstScreenSecondButton = (e) => {
        e.preventDefault();
        if (textSecondPopup) {
            setScreen(SECOND_POPUP);
            amplitude.getInstance().logEvent(PAYEE_PROCEED_DETAILS_CLICKED);
        } else {
            props.popupRef?.current?.close();
            props.sendParamToStateMachine ? props.sendParamToStateMachine({ type: 'NEXT' }) : props.setNextScreen(CALCULATOR_SCREEN);
            props.resetViewRatesFirst();
        }
    };

    const handleClickSecondScreenFirstButton = (e) => {
        e.preventDefault();
        props.popupRef?.current?.close()?.then(() => {
            setScreen(FIRST_POPUP);
        });
        amplitude.getInstance().logEvent(PAYEE_GO_BACK_EDIT_CLICKED);
    };

    const handleClickSecondScreenSecondButton = (e) => {
        e.preventDefault();
        props.popupRef?.current?.close()?.then(() => {
            setScreen(FIRST_POPUP);
        });
        amplitude.getInstance().logEvent(PAYEE_CONTINUE_ANYWAY_CLICKED);
        props.sendParamToStateMachine ? props.sendParamToStateMachine({ type: 'NEXT' }) : props.setNextScreen(CALCULATOR_SCREEN);
        props.resetViewRatesFirst();
    };

    const getText = (text) => {
        return <DangerouslyHtml dangerouslyHtml={text} />;
    };

    const render = (currentScreen) => {
        switch (currentScreen) {
            case FIRST_POPUP:
                return (
                    <SimplePopup popupRef={props.popupRef} text={getText(textFirstPopup)}>
                        <PopupButton iconName={calcFirstIcon} text={calcFirstLink} onClick={handleClickFirstScreenFirstButton} />
                        {!isBlocked && (
                            <PopupButton
                                iconName="arrow-forward"
                                isAlertButton={false}
                                text={props.bankAccountProceed}
                                onClick={handleClickFirstScreenSecondButton}
                            />
                        )}
                    </SimplePopup>
                );
            case SECOND_POPUP:
                return (
                    <SimplePopup popupRef={props.popupRef} text={getText(textSecondPopup)}>
                        <PopupButton iconName={'arrow-back-circular'} text={props.bankAccountEdit} onClick={handleClickSecondScreenFirstButton} />
                        {!isBlocked && (
                            <PopupButton
                                iconName="arrow-forward"
                                isAlertButton={true}
                                text={props.bankAccountContinue}
                                onClick={handleClickSecondScreenSecondButton}
                            />
                        )}
                    </SimplePopup>
                );
        }
    };

    return <>{render(screen)}</>;
};
const mapStateToProps = ({ messages: { pageMessages } }) => {
    return {
        useThisNameButton: pageMessages.useThisNameButton,
        bankAccountProceed: pageMessages.bankAccountProceed,
        bankAccountEdit: pageMessages.bankAccountEdit,
        bankAccountContinue: pageMessages.bankAccountContinue,
    };
};
PayeePopup.propTypes = {
    extra: PropTypes.shape({
        code: PropTypes.string,
        textFirstPopup: PropTypes.string.isRequired,
        textSecondPopup: PropTypes.string,
        useThisName: PropTypes.string,
        isBlocked: PropTypes.bool,
    }),
    bankAccountEdit: PropTypes.string.isRequired,
    bankAccountContinue: PropTypes.string.isRequired,
    bankAccountProceed: PropTypes.string.isRequired,
    useThisNameButton: PropTypes.string.isRequired,
    popupRef: PropTypes.shape({ current: PropTypes.object }),
    setNextScreen: PropTypes.func,
    sendParamToStateMachine: PropTypes.func,
    formName: PropTypes.string,
    isTransfers: PropTypes.bool,
    resetViewRatesFirst: PropTypes.func,
};

export default connect(mapStateToProps, { changeFormFieldValue })(PayeePopup);
